/**
 * The Soulstone: the account's ARPG identity slot (PRD #837).
 *
 * A Soulstone is a template a player takes up, not a save file keyed by class:
 * it holds a Hero plus that Hero's durable campaign progress, and two stones of
 * the same class with different builds are legal. The name is UO's own: a
 * Soulstone stored a skill template so one character could be many things.
 *
 * This module is pure Soulstone policy, the way `hero.ts` is Hero policy: what
 * a valid Soulstone record is, what a fresh one starts as, and how a durable id
 * is minted. Nothing here reads a world, an entity, a session, or a socket.
 */

import { createHero, type Hero } from '../hero';

/**
 * How many Soulstones an account may attune.
 *
 * The cap binds only attuning a new stone. The v13 migration grandfathers one
 * Soulstone per pre-existing Hero, so an account that already held four Heroes
 * keeps four stones; deleting player data was rejected as the alternative.
 */
export const SOULSTONE_SLOT_CAP = 3;

/** The three campaign difficulty tiers, in unlock order. */
export const CAMPAIGN_TIER_IDS = ['normal', 'nightmare', 'hell'] as const;
export type CampaignTierId = (typeof CAMPAIGN_TIER_IDS)[number];

export function isCampaignTierId(value: unknown): value is CampaignTierId {
  return CAMPAIGN_TIER_IDS.includes(value as CampaignTierId);
}

/** Durable per-tier campaign progress: what settled, never map-in-progress state. */
export interface SoulstoneTierProgress {
  /** Campaign node ids whose objective settled at this tier. Set-like. */
  completedNodeIds: string[];
  /** Campaign node ids whose waypoint was used at this tier. Set-like. */
  unlockedWaypointNodeIds: string[];
}

/**
 * Per-difficulty campaign progress carried by one Soulstone.
 *
 * Deliberately ledgers only: which tiers a stone may enter is **derived** from
 * them (`campaignTierUnlocked` in `campaignTravel.ts`, keyed on the prior
 * tier's final-node completion), never stored beside them. The v13 records
 * shipped with a `highestUnlockedTier` field that nothing ever advanced or
 * read; slice 7 removed it from the shape rather than start maintaining a
 * second truth, and the stray key on old rows is inert on read.
 */
export interface SoulstoneCampaign {
  tiers: Record<CampaignTierId, SoulstoneTierProgress>;
}

/**
 * One attuned Soulstone slot.
 *
 * The id is the durable identity every protocol message and store operation
 * keys on. It is unique within the account, minted at attunement, and stable
 * for the stone's whole life; the class is a property of the Hero inside, not
 * a key (PRD #837 user story thirteen).
 */
export interface Soulstone {
  id: string;
  hero: Hero;
  campaign: SoulstoneCampaign;
}

/** What a freshly attuned Soulstone has done: nothing, at Normal. */
export function emptySoulstoneCampaign(): SoulstoneCampaign {
  return {
    tiers: {
      normal: { completedNodeIds: [], unlockedWaypointNodeIds: [] },
      nightmare: { completedNodeIds: [], unlockedWaypointNodeIds: [] },
      hell: { completedNodeIds: [], unlockedWaypointNodeIds: [] },
    },
  };
}

/**
 * Attune one Soulstone: bind a fresh level-1 Hero of the chosen class to it.
 *
 * Class validity is `createHero`'s to answer, so an unknown or unplayable
 * class fails here exactly as it failed hero creation before Soulstones.
 */
export function attuneSoulstone(id: string, classId: unknown): Soulstone | null {
  if (typeof id !== 'string' || id.length === 0) return null;
  const hero = createHero(classId);
  if (!hero) return null;
  return { id, hero, campaign: emptySoulstoneCampaign() };
}

/**
 * Mint the next durable Soulstone id.
 *
 * The smallest unused `soulstone-<n>` rather than a counter field, so the mint
 * is a pure function of the ids already present: the migration, a replayed
 * migration, and a fresh attunement all agree without a second durable field.
 */
export function mintSoulstoneId(existingIds: readonly string[]): string {
  const taken = new Set(existingIds);
  for (let n = 1; ; n += 1) {
    const candidate = `soulstone-${n}`;
    if (!taken.has(candidate)) return candidate;
  }
}

function isTierProgress(value: unknown): value is SoulstoneTierProgress {
  if (value === null || typeof value !== 'object') return false;
  const progress = value as SoulstoneTierProgress;
  return (
    isStringSet(progress.completedNodeIds) &&
    isStringSet(progress.unlockedWaypointNodeIds)
  );
}

function isStringSet(value: unknown): boolean {
  if (!Array.isArray(value)) return false;
  const seen = new Set<string>();
  for (const entry of value) {
    if (typeof entry !== 'string' || entry.length === 0 || seen.has(entry)) {
      return false;
    }
    seen.add(entry);
  }
  return true;
}

/** Whether a stored campaign record is one this module could have written. */
export function isSoulstoneCampaign(value: unknown): value is SoulstoneCampaign {
  if (value === null || typeof value !== 'object') return false;
  const campaign = value as SoulstoneCampaign;
  if (campaign.tiers === null || typeof campaign.tiers !== 'object') return false;
  const keys = Object.keys(campaign.tiers);
  if (keys.length !== CAMPAIGN_TIER_IDS.length) return false;
  return CAMPAIGN_TIER_IDS.every((tier) => isTierProgress(campaign.tiers[tier]));
}
